import {
  BaseQueryFn,
  createApi,
  fetchBaseQuery,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react'
import { tokenStorage } from '@/app/entities/auth/lib/tokenStorage'
import { RefreshResponseDto } from '@/app/entities/auth/model/types'
import { LAYOUT } from '@/app/layouts/MainLayout/constants'

const AUTH_REFRESH_ENDPOINT = '/api/v1/auth/refresh'
const AUTH_LOGIN_ENDPOINT = '/api/v1/auth/login'

const rawBaseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_URL,
  prepareHeaders: (headers) => {
    const token = tokenStorage.getAccessToken()
    if (token) {
      headers.set('Authorization', `Bearer ${token}`)
    }
    return headers
  },
})

let refreshPromise: Promise<string | null> | null = null

export const refreshAccessToken = async (): Promise<string | null> => {
  const refreshToken = tokenStorage.getRefreshToken()
  if (!refreshToken) {
    return null
  }

  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}${AUTH_REFRESH_ENDPOINT}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      },
    )

    if (!response.ok) {
      return null
    }

    const data = (await response.json()) as RefreshResponseDto
    if (!data?.access_token) {
      return null
    }

    tokenStorage.setTokens(data.access_token)
    return data.access_token
  } catch {
    // Network error, aborted request, or malformed JSON — treat as a failed
    // refresh so the caller falls back to logout instead of crashing the query.
    return null
  }
}

const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const endpointUrl =
    typeof args === 'string' ? args : args.url
  const isAuthEndpoint =
    endpointUrl === AUTH_LOGIN_ENDPOINT || endpointUrl === AUTH_REFRESH_ENDPOINT

  let result = await rawBaseQuery(args, api, extraOptions)

  if (result.error?.status === 401 && !isAuthEndpoint) {
    if (!refreshPromise) {
      refreshPromise = refreshAccessToken().finally(() => {
        refreshPromise = null
      })
    }
    const newToken = await refreshPromise

    if (newToken) {
      result = await rawBaseQuery(args, api, extraOptions)
    } else {
      tokenStorage.clear()
      if (
        typeof window !== 'undefined'
        && window.location.pathname !== LAYOUT.ROUTES.LOGIN
      ) {
        window.location.replace(LAYOUT.ROUTES.LOGIN)
      }
    }
  }
  return result
}

// DEV-MOCK (AGB-401) — start. `?mock=finance` answers requests from fixtures
// in dev builds only; `import.meta.env.DEV` is `false` in a production build,
// so this branch and the fixture chunk are compiled out. Remove with
// `src/app/entities/finance/__fixtures__/`.
const baseQuery: typeof baseQueryWithReauth = import.meta.env.DEV
  ? async (args, api, extraOptions) => {
      const { resolveDevMock } = await import(
        '@/app/entities/finance/__fixtures__/devMock'
      )
      const mocked = await resolveDevMock(args)
      return mocked ?? baseQueryWithReauth(args, api, extraOptions)
    }
  : baseQueryWithReauth
// DEV-MOCK — end.

export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery,
  tagTypes: [
    'Odds',
    'VirtualSessionsList',
    'VirtualSession',
    'VirtualSessionsOverview',
    'VirtualSessionBets',
    'VirtualSessionAnalytics',
    'VirtualSessionConfig',
    'VirtualSessionConfigSchema',
    // AGB-483: a twin's bet-by-bet comparison with its real session.
    'VirtualSessionParity',
    // AGB-497 (AGB-490 rework): prematch virtual sessions, served by their own
    // `/prematch_sessions` endpoints, so their cache never touches a live one.
    'PrematchSessionsList',
    'PrematchSessionsOverview',
    'PrematchSessionBets',
    'PrematchSessionAnalytics',
    'PrematchSessionConfig',
    'PrematchSessionConfigSchema',
    // AGB-499: the cross-session analytics summary (read-only).
    'SessionAnalytics',
    'RealAccountsList',
    'RealSessionsList',
    'RealSession',
    'RealSessionConfig',
    'RealSessionConfigSchema',
    'RealSessionAccounts',
    'RealSessionOverview',
    'RealSessionBets',
    'RealSessionAnalytics',
    'RealSessionPendingOrders',
    'RealSessionCapitalMovements',
    'RealSessionDetectionFunnel',
    'SessionConfigVersions',
    'AlertRules',
    'RealBotsList',
    'KillSwitches',
    'RealApprovalsList',
    'RealApproval',
    'AuditLog',
    'CalibrationReport',
    'CalibrationWatchlist',
    'CurrentUser',
    'ConsumerHealth',
    'FinanceManualTopups',
    'FinanceDepositAddress',
    'FinanceRecentTransactions',
    'FinanceWallets',
    'FinanceTransactions',
    'FinanceSettings',
    'FinanceSettingsSchema',
    'FinanceFundingDeposits',
    // Covers both the available-to-withdraw figure and the withdrawal history:
    // a confirmed withdrawal changes both, and there is no state in which one
    // of the two is stale while the other is not.
    'FinanceWithdrawals',
    // Finance › Overview v2 (AGB-401): one tag for all six overview queries.
    'FinanceOverviewV2',
  ],
  endpoints: () => ({}),
})